import Flickity from 'flickity';
import { Accordion } from './accordion';

/* eslint-disable no-restricted-syntax */
const keyCode = Object.freeze({
	PAGEUP: 33,
	PAGEDOWN: 34,
	END: 35,
	HOME: 36,
	LEFT: 37,
	UP: 38,
	RIGHT: 39,
	DOWN: 40,
	DELETE: 46,
	ENTER: 13,
	SPACE: 32,
	RETURN: 13,
});

const direction = Object.freeze({
	37: -1,
	38: -1,
	39: 1,
	40: 1,
});

class TabList {
	constructor(domNode) {
		this.domNode = domNode;
		this.tabs = [];
		this.panels = [];
	}

	init() {
		this.tabs = this.domNode.querySelectorAll('[role="tab"]');
		this.panels = this.domNode.querySelectorAll('[role="tabpanel"]');

		for (let i = 0; i < this.tabs.length; ++i) {
			const tab = this.tabs[i];
			tab.addEventListener('click', this.handleClick.bind(this));
			tab.addEventListener('keydown', this.handleKeydown.bind(this));
			tab.addEventListener('keyup', this.handleKeyup.bind(this));
			tab.index = i;
		}
	}

	handleClick(event) {
		const tab = event.currentTarget;
		this.activateTab(tab, false);
	}

	handleKeydown(event) {
		let flag = false;

		switch (event.keyCode) {
			case keyCode.HOME:
			case keyCode.PAGEUP:
				this.focusFirstTab();
				flag = true;
				break;

			case keyCode.END:
			case keyCode.PAGEDOWN:
				this.focusLastTab();
				flag = true;
				break;

			default:
				break;
		}

		if (flag) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleKeyup(event) {
		let flag = false;

		switch (event.keyCode) {
			case keyCode.LEFT:
			case keyCode.RIGHT:
				this.determineOrientation(event);
				flag = true;
				break;

			case keyCode.ENTER:
			case keyCode.RETURN:
			case keyCode.SPACE:
				this.activateTab(event.currentTarget);
				flag = true;
				break;

			default:
				break;
		}

		if (flag) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	activateTab(tab, setFocus = false) {
		this.deactivateTabs();

		tab.removeAttribute('tabindex');
		tab.setAttribute('aria-selected', 'true');
		tab.classList.add('active');

		const controls = tab.getAttribute('aria-controls');
		const controledEl = document.querySelector(`#${controls}`);
		controledEl.removeAttribute('hidden');
		controledEl.removeAttribute('aria-hidden');

		if (controledEl.querySelector('.img-carousel')) {
			const gCarouselEls = controledEl.querySelector('.img-carousel');
			const gCarouselFlickity = Flickity.data(gCarouselEls);
			gCarouselFlickity.resize();
		}

		if (setFocus) {
			tab.focus();
		}
	}

	activateTabs() {
		for (const tab of this.tabs) {
			tab.removeAttribute('tabindex');
			tab.setAttribute('aria-selected', 'true');
		}

		for (const panel of this.panels) {
			panel.removeAttribute('hidden');
			panel.removeAttribute('aria-hidden');
		}
	}

	deactivateTabs() {
		for (const tab of this.tabs) {
			tab.setAttribute('tabindex', '-1');
			tab.setAttribute('aria-selected', 'false');
			tab.classList.remove('active');
		}

		for (const panel of this.panels) {
			panel.setAttribute('hidden', 'hidden');
			panel.setAttribute('aria-hidden', true);
		}
	}

	determineOrientation(event) {
		const keyPressed = event.keyCode;
		let proceed = false;

		if (keyPressed === keyCode.LEFT || keyPressed === keyCode.RIGHT) {
			proceed = true;
		}

		if (proceed) {
			this.switchTabOnArrowPress(event);
		}
	}

	switchTabOnArrowPress(event) {
		const keyPressed = event.keyCode;

		if (direction[keyPressed]) {
			const target = event.currentTarget;

			if (target.index !== undefined) {
				if (this.tabs[target.index + direction[keyPressed]]) {
					this.tabs[target.index + direction[keyPressed]].focus();
				} else if (keyPressed === keyCode.LEFT) {
					this.focusLastTab();
				} else if (keyPressed === keyCode.RIGHT) {
					this.focusFirstTab();
				}
			}
		}
	}

	focusFirstTab() {
		this.tabs[0].focus();
	}

	focusLastTab() {
		this.tabs[this.tabs.length - 1].focus();
	}
}

const mediaBreakPoint = '(max-width: 767px)';
const mq = window.matchMedia(mediaBreakPoint);

const tabsBlockEls = document.querySelectorAll('.tabs-block');

tabsBlockEls.forEach((tabsBlockEl) => {
	const createTabTriggerEl = (tabs, parentEl) => {
		tabs.forEach((tab, idx) => {
			const liEl = document.createElement('li');
			const buttonEl = document.createElement('button');

			liEl.setAttribute('role', 'presentation');
			buttonEl.classList.add('tab-button');
			buttonEl.setAttribute('role', 'tab');
			buttonEl.setAttribute('aria-selected', idx === 0 ? 'true' : 'false');
			buttonEl.setAttribute('aria-controls', tab.panelid);
			buttonEl.setAttribute('id', `${tab.panelid}TabTrigger`);
			if (idx === 0) {
				buttonEl.classList.add('active');
			} else {
				buttonEl.setAttribute('tabindex', '-1');
			}
			buttonEl.appendChild(document.createTextNode(tab.tabName));

			liEl.appendChild(buttonEl);
			parentEl.appendChild(liEl);
		});
	};

	const tabPanelEls = tabsBlockEl.querySelectorAll('.tab-panel');
	const tabsData = Array.from(tabPanelEls).map((el) => ({
		panelid: el.getAttribute('id'),
		tabName: el.getAttribute('data-tabname'),
		tabimagealttext: el.getAttribute('data-tabimagealttext'),
	}));

	createTabTriggerEl(tabsData, tabsBlockEl.querySelector('.tabs'));

	const prevWidth = window.innerWidth;
	let initTabs = false;
	let initAccordion = false;
	let setActiveTab = false;
	let setActoveAccordion = false;
	let tabBlockTabList = null;
	let tabAccordion = null;

	const resizeHandler = () => {
		if (!mq.matches && !initTabs) {
			initTabs = true;
			tabBlockTabList = new TabList(tabsBlockEl);
			tabBlockTabList.init();
		}

		if (mq.matches && !initAccordion) {
			initAccordion = true;
			const tabAccordionEl = tabsBlockEl.querySelector('.mobile-tabs-accordion');
			tabAccordion = new Accordion(tabAccordionEl);
			tabAccordion.init();
		}

		if (!mq.matches && !setActiveTab) {
			setActiveTab = true;
			setActoveAccordion = false;
			const allPanelEls = tabsBlockEl.querySelectorAll('.accordion-panel');
			let activeTab = tabsBlockEl.querySelector('.tabs li:first-child .tab-button');
			for (const el of allPanelEls) {
				if (!el.hasAttribute('hidden')) {
					activeTab = document.querySelector(`#${el.getAttribute('id')}TabTrigger`);
					break;
				}
			}
			tabBlockTabList.activateTab(activeTab, false);
		}

		if (mq.matches && !setActoveAccordion) {
			setActoveAccordion = true;
			setActiveTab = false;
			const allPanelEls = tabsBlockEl.querySelectorAll('.accordion-panel');
			let activeTab = tabsBlockEl.querySelector('.tab-panels-container .accordion-item:first-child .accordion-trigger');
			for (const el of allPanelEls) {
				if (!el.hasAttribute('hidden')) {
					activeTab = document.querySelector(`#${el.getAttribute('aria-labelledby')}`);
					break;
				}
			}
			tabAccordion.activateAccordionItem(activeTab);
		}
	};

	resizeHandler();
	window.addEventListener('resize', function () {
		if (window.innerWidth === prevWidth) {
			return;
		}
		resizeHandler();
	});
});
